(() => {
  if (customElements.get('bh-free-shipping')) return;
  const messages = {
    cs: { left: (m, c) => `Do dopravy zdarma přes ${c} vám zbývá ${m}.`, reached: c => `Dosáhli jste hranice dopravy zdarma přes ${c}.`, note: c => `Pro doručení do země: ${c}. Po slevách. Dostupnost a podmínky potvrdí pokladna.` },
    en: { left: (m, c) => `Add ${m} for free shipping with ${c}.`, reached: c => `You have reached the free shipping threshold with ${c}.`, note: c => `Delivery country: ${c}. After discounts. Availability and conditions are confirmed at checkout.` },
    de: { left: (m, c) => `Noch ${m} bis zum kostenlosen Versand mit ${c}.`, reached: c => `Sie haben die Grenze für kostenlosen Versand mit ${c} erreicht.`, note: c => `Lieferland: ${c}. Nach Rabatten. Verfügbarkeit und Bedingungen werden beim Checkout bestätigt.` }
  };
  const cache = new Map();
  let revision = 0;
  let cartController;
  let timer;
  function carrier(name) {
    const n = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    for (const [re, label] of [[/balikovna/, 'Balíkovna'], [/\bdpd\b/, 'DPD'], [/\bgls\b/, 'GLS'], [/\bone\b/, 'ONE'], [/\bppl\b/, 'PPL'], [/zasilkovna/, 'Zásilkovna'], [/packeta/, 'Packeta'], [/inpost/, 'InPost'], [/hermes/, 'Hermes PP']]) if (re.test(n)) return label;
    return name;
  }
  function convert(value, currency, shopCurrency, settings) {
    const amount = Number(value.amount);
    if (!Number.isFinite(amount) || amount < 0) return null;
    if (value.currencyCode === currency) return amount;
    const rate = Number(settings?.rate);
    return value.currencyCode === shopCurrency && settings?.active === currency && Number.isFinite(rate) && rate > 0 ? amount * rate : null;
  }
  // Only a single general profile can be evaluated without product/profile mappings.
  function evaluate(data, cart, shopCurrency, settings) {
    if (!cart.item_count || !Number.isFinite(cart.total_price) || !Number.isFinite(cart.total_weight) || !cart.items?.some(i => i.requires_shipping)) return null;
    if (data.groups?.length !== 1 || !data.groups[0].isDefault) return null;
    const candidates = [];
    for (const zone of data.groups[0].zones) {
      if (zone.countries.some(c => c.provinces?.length)) continue;
      for (const rate of zone.rates) {
        if (!rate.price || Number(rate.price.amount) !== 0 || rate.calculated) continue;
        let minimum = 0, maximum = Infinity, valid = true;
        for (const condition of rate.conditions || []) {
          const lower = condition.operator === 'GREATER_THAN_OR_EQUAL_TO';
          const upper = condition.operator === 'LESS_THAN_OR_EQUAL_TO';
          if (!lower && !upper) { valid = false; break; }
          const value = condition.value;
          if (condition.field === 'TOTAL_PRICE' && value?.__typename === 'MoneyV2') {
            const amount = convert(value, cart.currency, shopCurrency, settings);
            if (amount === null) { valid = false; break; }
            // Ceiling for minimum spend, floor for maximum; shipping-price rounding does not apply.
            if (lower) minimum = Math.max(minimum, Math.ceil(amount * 100 - 1e-7));
            else maximum = Math.min(maximum, Math.floor(amount * 100 + 1e-7));
          } else if (condition.field === 'TOTAL_WEIGHT' && value?.__typename === 'Weight') {
            const multiplier = { GRAMS: 1, KILOGRAMS: 1000, POUNDS: 453.59237, OUNCES: 28.349523125 }[value.unit];
            const grams = Number(value.value) * multiplier;
            if (!Number.isFinite(grams) || (lower ? cart.total_weight < grams : cart.total_weight > grams)) { valid = false; break; }
          } else { valid = false; break; }
        }
        if (valid && minimum <= maximum && cart.total_price <= maximum) candidates.push({ minimum, name: carrier(rate.name) });
      }
    }
    if (!candidates.length) return null;
    const reached = candidates.filter(c => cart.total_price >= c.minimum);
    const minimum = reached.length ? Math.min(...reached.map(c => c.minimum)) : Math.min(...candidates.map(c => c.minimum));
    const chosen = reached.length ? reached : candidates.filter(c => c.minimum === minimum);
    return { remaining: Math.max(0, minimum - cart.total_price), progress: minimum ? Math.min(100, cart.total_price / minimum * 100) : 100, names: [...new Set(chosen.map(c => c.name))].join(', ') };
  }
  async function rates(root) {
    const key = root.dataset.endpoint + ':' + root.dataset.country;
    const entry = cache.get(key);
    if (entry && Date.now() - entry.time < 300000) return entry.promise;
    const url = new URL(root.dataset.endpoint);
    url.searchParams.set('country', root.dataset.country);
    const promise = fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(15000) }).then(async response => {
      if (!response.ok) throw new Error('Shipping unavailable');
      const data = await response.json();
      if (!data.ok || !Array.isArray(data.groups)) throw new Error('Invalid shipping data');
      return data;
    }).catch(error => { cache.delete(key); throw error; });
    cache.set(key, { time: Date.now(), promise });
    return promise;
  }
  async function refresh() {
    const current = ++revision;
    cartController?.abort();
    const roots = [...document.querySelectorAll('bh-free-shipping')];
    if (!roots.length) return;
    roots.forEach(root => { root.hidden = true; });
    cartController = new AbortController();
    const controller = cartController;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(base + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      await Promise.all(roots.map(async root => {
        try {
          const data = await rates(root);
          if (current !== revision || !root.isConnected) return;
          const state = evaluate(data, cart, root.dataset.shopCurrency, window.Shopify?.currency);
          if (!state) return;
          const locale = root.dataset.locale || 'cs';
          const t = messages[locale.split('-')[0]] || messages.en;
          const money = new Intl.NumberFormat(locale, { style: 'currency', currency: cart.currency }).format(state.remaining / 100);
          root.querySelector('.bh-free-shipping__message').textContent = state.remaining ? t.left(money, state.names) : t.reached(state.names);
          root.querySelector('.bh-free-shipping__note').textContent = t.note(root.dataset.countryName);
          root.querySelector('.bh-free-shipping__track span').style.width = state.progress + '%';
          root.classList.toggle('bh-free-shipping--reached', state.remaining === 0);
          root.hidden = false;
        } catch { /* Keep checkout usable when shipping data is unavailable. */ }
      }));
    } catch { /* The widget stays hidden; normal cart functions are unaffected. */ }
    finally { clearTimeout(timeout); }
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(refresh, 100); }
  customElements.define('bh-free-shipping', class extends HTMLElement { connectedCallback() { schedule(); } });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('bh:cart-open', schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
})();
